import React, { useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Badge,
  Stack,
  Tab,
  Tabs,
} from '@openedx/paragon';
import { getConfig } from '@edx/frontend-platform';
import { useIntl } from '@edx/frontend-platform/i18n';
import { getAuthenticatedUser } from '@edx/frontend-platform/auth';
import { useNavigate, useLocation } from 'react-router-dom';

import { getLoadingStatuses, getStudioHomeData, getSelectedCompanyOrPlatformKey } from '../data/selectors';
import messages from './messages';
import LibrariesTab from './libraries-tab';
import LibrariesV2Tab from './libraries-v2-tab/index';
import ArchivedTab from './archived-tab';
import CoursesTab from './courses-tab';
import ProgramsTab from './programs-tab';
import CompanyManagementPortalTab from './company-management-portal-tab';
import LtiToolsTab from './lti-tools-tab';
import AiAssistedCourseAuthoringTab from './ai-assisted-course-authoring-tab';
import {
  fetchLibraryData,
  fetchPlatformData,
  fetchCompanyManagementGeneralData,
  fetchCompanyManagementSpecificData,
  fetchCompanyCatalog,
  fetchLegacyLicenses,
  fetchProgramData,
} from '../data/thunks';
import { RequestStatus } from '../../data/constants';

const TabsSection = ({
  showNewCourseContainer,
  onClickNewCourse,
  isShowProcessing,
  isPaginationCoursesEnabled,
  librariesV1Enabled,
  librariesV2Enabled,
  programsEnabled,
}) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const sharedSelectionKey = useSelector(getSelectedCompanyOrPlatformKey);

  // NOTE: This check does not update if a user's permission is changed. The user will have to log out and back in.
  const isStaff = getAuthenticatedUser().administrator;

  const TABS_LIST = {
    courses: 'courses',
    libraries: 'libraries',
    legacyLibraries: 'legacyLibraries',
    archived: 'archived',
    taxonomies: 'taxonomies',
    companyManagementPortal: 'companyManagementPortal',
    pathways: 'pathways',
    ltiTools: 'ltiTools',
    aiAssistedCourseAuthoring: 'aiAssistedCourseAuthoring',
  } as const;

  const initTabKeyState = (pname) => {
    if (pname.includes('/libraries-v1')) {
      return TABS_LIST.legacyLibraries;
    }

    if (pname.includes('/libraries')) {
      return librariesV2Enabled
        ? TABS_LIST.libraries
        : TABS_LIST.legacyLibraries;
    }

    if (pname.includes('/company-management-portal')) {
      return TABS_LIST.companyManagementPortal;
    }

    if (pname.includes('/pathways')) {
      return TABS_LIST.pathways;
    }

    if (pname.includes('/lti-tools')) {
      return TABS_LIST.ltiTools;
    }

    if (pname.includes('/ai-assisted-course-authoring')) {
      return TABS_LIST.aiAssistedCourseAuthoring;
    }

    // Default to courses tab
    return TABS_LIST.courses;
  };

  const [tabKey, setTabKey] = useState(initTabKeyState(pathname));

  // When a company/platform is selected from either tab, fetch all shared company data
  useEffect(() => {
    if (sharedSelectionKey) {
      dispatch(fetchCompanyManagementSpecificData(sharedSelectionKey));
      dispatch(fetchCompanyCatalog(sharedSelectionKey));
      dispatch(fetchLegacyLicenses(sharedSelectionKey));
      dispatch(fetchProgramData(sharedSelectionKey));
    }
  }, [sharedSelectionKey]);

  // This is needed to handle navigating using the back/forward buttons in the browser
  useEffect(() => {
    // Handle special case when navigating directly to /libraries-v1
    // we need to call dispatch to fetch library data
    if (pathname.includes('/libraries-v1')) {
      dispatch(fetchLibraryData());
    }
    if (pathname.includes('/company-management-portal')) {
      // No action needed currently
      dispatch(fetchCompanyManagementGeneralData());
    }
    // Handle special case when navigating directly to /programs
    // we need to call dispatch to fetch platform data
    if (pathname.includes('/pathways')) {
      dispatch(fetchPlatformData());
    }
    setTabKey(initTabKeyState(pathname));
  }, [pathname]);

  const {
    courses, libraries, archivedCourses,
    numPages, coursesCount,
  } = useSelector(getStudioHomeData);
  const {
    courseLoadingStatus,
    libraryLoadingStatus,
    companyManagementPortalLoadingStatus,
    platformLoadingStatus,
  } = useSelector(getLoadingStatuses);
  const isLoadingCourses = courseLoadingStatus === RequestStatus.IN_PROGRESS;
  const isFailedCoursesPage = courseLoadingStatus === RequestStatus.FAILED;
  const isLoadingLibraries = libraryLoadingStatus === RequestStatus.IN_PROGRESS;
  const isFailedLibrariesPage = libraryLoadingStatus === RequestStatus.FAILED;
  const isLoadingCompanyManagementPortal = companyManagementPortalLoadingStatus === RequestStatus.IN_PROGRESS;
  const isFailedCompanyManagementPortal = companyManagementPortalLoadingStatus === RequestStatus.FAILED;
  const isLoadingPlatforms = platformLoadingStatus === RequestStatus.IN_PROGRESS;
  const isFailedPlatformsPage = platformLoadingStatus === RequestStatus.FAILED;

  // Controlling the visibility of tabs when using conditional rendering is necessary for
  // the correct operation of iterating over child elements inside the Paragon Tabs component.
  const visibleTabs = useMemo(() => {
    const tabs: JSX.Element[] = [];
    tabs.push(
      <Tab
        key={TABS_LIST.courses}
        eventKey={TABS_LIST.courses}
        title={intl.formatMessage(messages.coursesTabTitle)}
      >
        <CoursesTab
          coursesDataItems={courses}
          showNewCourseContainer={showNewCourseContainer}
          onClickNewCourse={onClickNewCourse}
          isShowProcessing={isShowProcessing}
          isLoading={isLoadingCourses}
          isFailed={isFailedCoursesPage}
          numPages={numPages}
          coursesCount={coursesCount}
          isEnabledPagination={isPaginationCoursesEnabled}
        />
      </Tab>,
    );

    if (archivedCourses?.length && isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.archived}
          eventKey={TABS_LIST.archived}
          title={intl.formatMessage(messages.archivedTabTitle)}
        >
          <ArchivedTab
            archivedCoursesData={archivedCourses}
            isLoading={isLoadingCourses}
            isFailed={isFailedCoursesPage}
          />
        </Tab>,
      );
    }

    if (librariesV2Enabled && isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.libraries}
          eventKey={TABS_LIST.libraries}
          title={(
            <Stack gap={2} direction="horizontal">
              {intl.formatMessage(messages.librariesTabTitle)}
              <Badge variant="info">{intl.formatMessage(messages.librariesV2TabBetaBadge)}</Badge>
            </Stack>
          )}
        >
          <LibrariesV2Tab />
        </Tab>,
      );
    }

    if (librariesV1Enabled && isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.legacyLibraries}
          eventKey={TABS_LIST.legacyLibraries}
          title={intl.formatMessage(
            librariesV2Enabled
              ? messages.legacyLibrariesTabTitle
              : messages.librariesTabTitle,
          )}
        >
          <LibrariesTab
            libraries={libraries}
            isLoading={isLoadingLibraries}
            isFailed={isFailedLibrariesPage}
          />
        </Tab>,
      );
    }

    if (getConfig().ENABLE_TAGGING_TAXONOMY_PAGES === 'true' && isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.taxonomies}
          eventKey={TABS_LIST.taxonomies}
          title={intl.formatMessage(messages.taxonomiesTabTitle)}
        />,
      );
    }

    if (isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.companyManagementPortal}
          eventKey={TABS_LIST.companyManagementPortal}
          title={intl.formatMessage(messages.companyManagementPortal)}
        >
          <CompanyManagementPortalTab
            isLoading={isLoadingCompanyManagementPortal} // switch to loading company data
            isFailed={isFailedCompanyManagementPortal} // switch to failed company data
          />
        </Tab>,
      );
    }

    if (isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.aiAssistedCourseAuthoring}
          eventKey={TABS_LIST.aiAssistedCourseAuthoring}
          title={intl.formatMessage(messages.aiAssistedCourseAuthoringTabTitle)}
        >
          <AiAssistedCourseAuthoringTab />
        </Tab>,
      );
    }

    if (programsEnabled === true && isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.pathways}
          eventKey={TABS_LIST.pathways}
          title={intl.formatMessage(messages.programsTabTitle)}
        >
          <ProgramsTab
            isLoading={isLoadingPlatforms}
            isFailed={isFailedPlatformsPage}
          />
        </Tab>,
      );
    }

    if (isStaff) {
      tabs.push(
        <Tab
          key={TABS_LIST.ltiTools}
          eventKey={TABS_LIST.ltiTools}
          title={intl.formatMessage(messages.ltiToolsTabTitle)}
        >
          <LtiToolsTab />
        </Tab>,
      );
    }

    return tabs;
  }, [
    archivedCourses,
    showNewCourseContainer,
    isLoadingCourses,
    isLoadingLibraries,
    isLoadingPlatforms,
    isFailedPlatformsPage,
    isLoadingCompanyManagementPortal,
  ]);

  const handleSelectTab = (tab) => {
    if (tab === TABS_LIST.courses) {
      navigate('/home');
    } else if (tab === TABS_LIST.legacyLibraries) {
      dispatch(fetchLibraryData());
      navigate('/libraries-v1');
    } else if (tab === TABS_LIST.libraries) {
      navigate('/libraries');
    } else if (tab === TABS_LIST.taxonomies) {
      navigate('/taxonomies');
    } else if (tab === TABS_LIST.companyManagementPortal) {
      navigate('/company-management-portal');
    } else if (tab === TABS_LIST.pathways) {
      dispatch(fetchPlatformData());
      navigate('/pathways');
    } else if (tab === TABS_LIST.ltiTools) {
      navigate('/lti-tools');
    } else if (tab === TABS_LIST.aiAssistedCourseAuthoring) {
      navigate('/ai-assisted-course-authoring');
    }
    setTabKey(tab);
  };

  return (
    <Tabs
      className="studio-home-tabs"
      variant="tabs"
      activeKey={tabKey}
      onSelect={handleSelectTab}
    >
      {visibleTabs}
    </Tabs>
  );
};

TabsSection.defaultProps = {
  isPaginationCoursesEnabled: false,
};

TabsSection.propTypes = {
  showNewCourseContainer: PropTypes.bool.isRequired,
  onClickNewCourse: PropTypes.func.isRequired,
  isShowProcessing: PropTypes.bool.isRequired,
  isPaginationCoursesEnabled: PropTypes.bool,
  librariesV1Enabled: PropTypes.bool,
  librariesV2Enabled: PropTypes.bool,
  programsEnabled: PropTypes.bool,
};

export default TabsSection;
